import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  value: string; // HH:mm (24h)
  onChange: (v: string) => void;
  step?: number; // minutes between slots
  label?: string;
};

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Snap an "HH:mm" value to the nearest slot on the `step`-minute grid, so the
 * picker can preselect + scroll to it even when the value is off-grid — the
 * reading sheets default to the exact current time (e.g. 18:57), which is not a
 * 5-min slot. Rounds to the nearest step and clamps to the last slot of the day
 * (never rolls over to a non-existent 24:00). Returns null for an unparseable
 * value (e.g. the empty "--:--" placeholder).
 */
export function nearestSlot(value: string, step: number): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const total = Number(m[1]) * 60 + Number(m[2]);
  const lastSlot = Math.floor((24 * 60 - 1) / step) * step;
  const snapped = Math.min(Math.max(Math.round(total / step) * step, 0), lastSlot);
  return `${pad(Math.floor(snapped / 60))}:${pad(snapped % 60)}`;
}

/**
 * Keyboard-free time picker for the driver reading sheets. A button trigger
 * (styled to match the sheet's other fields) opens a modal of tappable time
 * chips at `step`-minute intervals, scrolled to the current value. Mirrors the
 * operator app's "Select a time" picker; driver-themed (light + dark) and never
 * raises the system keyboard. No free-minute entry — values snap to `step`.
 */
export function TimeField({ value, onChange, step = 5, label = 'Select a time' }: Props) {
  const [open, setOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const slots: string[] = [];
  for (let m = 0; m < 24 * 60; m += step) slots.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);

  // The current value (often the exact "now", e.g. 18:57) snapped onto the grid,
  // so the nearest real slot (18:55) is the one we highlight + scroll to.
  const nearest = nearestSlot(value, step);

  // Open scrolled to the nearest slot (or the top) so the relevant times are
  // in view without a long scroll.
  useEffect(() => {
    if (!open) return;
    const el = gridRef.current;
    if (!el) return;
    const target = nearest ? (el.querySelector(`[data-time='${nearest}']`) as HTMLElement | null) : null;
    (target ?? (el.firstElementChild as HTMLElement | null))?.scrollIntoView?.({ block: 'center' });
  }, [open, nearest]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="w-full px-3 py-3 text-xl text-center font-mono tracking-widest bg-[var(--color-surface-soft)] border-2 border-[var(--color-border,#d1d5db)] rounded-2xl text-[var(--color-text)] outline-none active:border-[var(--green-700,#1B6A41)]"
      >
        {value || '--:--'}
      </button>

      {open ? createPortal(
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          role="dialog"
          aria-label={label}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 18,
              width: 'min(360px, 88vw)',
              maxHeight: '78vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--color-border)' }}>
              <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>{label}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, color: 'var(--color-text-secondary)', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            <div ref={gridRef} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: 14, overflowY: 'auto' }}>
              {slots.map((s) => {
                const selected = s === nearest;
                return (
                  <button
                    key={s}
                    type="button"
                    data-time={s}
                    aria-pressed={selected}
                    onClick={() => {
                      onChange(s);
                      setOpen(false);
                    }}
                    style={{
                      padding: '11px 0',
                      borderRadius: 10,
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      border: selected ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                      background: selected ? 'var(--mint-soft)' : 'var(--color-surface)',
                      color: selected ? 'var(--green-700)' : 'var(--color-text)',
                    }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
